
<template>
    <article
            :class="[
                'r-card',
                { 'r-card--uploaded' : isUploadCard },
                { 'r-card--failed' : failed },
                { 'r-card--no-meta' : !meta || meta.length === 0 }

            ]"
            @click="handleClick"
        >
        <aside class="r-card__edit-alt" v-if="isAdmin && (!meta || meta.length === 0)">
            <a class="r-card__edit" href="" @click.prevent.stop="editResource($props)">Edit</a>
        </aside>

        <header class="r-card__header">
            <div @click.stop="" class="r-card__checkbox form__checkbox form__group" v-if="type == 'document' && !isUploadCard">
                <label class="sr-only">Select for multiple download</label>
                <input type="checkbox" v-model="actionCheckbox" name="download">
                <span><SVGicon-tick /></span>
            </div>
            <h4 class="r-card__title">{{ name }}</h4>
            <a class="r-card__edit" href="" v-if="isAdmin && meta && meta.length > 0" @click.prevent.stop="editResource($props)">Edit</a>
        </header>

        <section class="r-card__meta" v-if="meta && meta.length > 0">
            <div
                class="r-card__meta-item"
                v-for="({ name: metaName, type }) in meta"
                :key="`${name}-meta-${type}-${metaName}`"
            >
                <span>{{ type == 'Area' && is_page_only ? 'Page' : type }}: </span>

                <template v-if="type == 'Area' && !is_page_only">
                    <span style="color: black;" v-if="metaName == 'All Areas'">{{ metaName | titleCase }}</span>
                    <a
                        v-else
                        class="r-card__link"
                        :href="`/areas/${areaLink}`"
                        @click.prevent.stop="$router.push({ name: 'area', params: { area: areaLink, tab: category } })">
                        {{ metaName | titleCase }}
                    </a>
                </template>

                <template v-if="type == 'Area' && is_page_only">
                    <span style="color: black;">{{ assignedPage[0].name }}</span>
                </template>

                <template v-if="type !== 'Area'">{{ metaName | titleCase }}</template>
            </div>

            <slot name="meta" />
        </section>

        <footer class="r-card__actions">
            <component :is="icon" v-if="!isUploadCard" />
        </footer>
    </article>
</template>

<script>
import { mapState, mapGetters, mapActions } from 'vuex'
import { toTitleCase } from '@/helpers'

export default {
    inheritAttrs: false,

    props: {
        id: { type: String, required: true },
        name: { type: String, required: true },
        category: { type: String },
        area: { type: String },
        group: { type: String },
        jurisdiction: { type: String },
        type: { type: String, required: true },
        url: { type: String, required: true },
        showArea: { type: Boolean, default: false },
        isUploadCard: { type: Boolean, default: false },
        showAllMeta: { type: Boolean, default: false }, // used for "ALL search results"
        // Following are used for admin editing panel...
        area_slug: { type: String },
        author: { type: String },
        documenttype: { type: String },
        filename: { type: String },
        filepath: { type: String },
        filesize: { type: [Number, String] },
        tags: { type: [Array, String] },
        tags_nouns: { type: [Array, String] },
        title: { type: String },
        _id: { type: String },
        failed: { type: Boolean, default: false },
        is_page_only: { type: [Boolean, String], default: false },
        page_category: { type: String },
        page: { type: [String, Number] },
        childPage: { type: [String, Number] },
        isSearch: { type: Boolean },
        resource_type: { type: String },
        document_type: { type: String },
    },

    data() {
        return {
            downloadable: true,
            icons: {
                download: () => import('@/assets/svgs/icon-download'),
                external: () => import('@/assets/svgs/icon-external'),
                video: () => import('@/assets/svgs/icon-play'),
            }
        }
    },

    filters: {
        titleCase (val) {
            return val ? toTitleCase(val.split('-').join(' ')) : val
        }
    },

    computed: {
        ...mapGetters('Auth', ['isAdmin']),
        ...mapState('Auth', ['user']),
        ...mapState('App', ['pages']),
        ...mapState('Resources', ['zipFiles', 'areas']),

        assignedPage () {
            if (this.is_page_only) {
                return this.pages.filter(({id}) => id === this.page)
            }
            return null
        },

        fileIsSelected () {
            // We need the boolean value for the checkbox,
            // so we use the filter to see if this component is selected
            return this.zipFiles && this.zipFiles.filter(file => file.id === this.id).length > 0
        },

        areaLink () {
            if (!this.area_slug) {
                const area = this.areas
                    .filter(({ name, path }) => name === this.area || path === this.area)[0]
                return area ? area.path : 'All'
            }

            return this.area_slug
        },

        actionCheckbox: {
            get () { return this.fileIsSelected },
            set () {
                const { id, name, filename, category, area, group } = this
                const fileData = { id, name, filename, category, area, group }

                if (this.is_page_only) {
                    fileData.area = this.assignedPage
                    fileData.category = this.page_category
                    fileData.group = 'Page Resource'
                }

                if (this.fileIsSelected) this.removeFromZip(id)
                else {
                    this.addToZip(fileData)
                }
            }
        },

        cardType () {
            return 'document'
        },

        icon () {
            switch (this.type) {
                case 'document': return this.icons.download
                case 'external': return this.icons.external
                case 'video': return this.icons.video
                default: return this.icons.download
            }
        },

        meta () {
            const defaultMeta = []

            if (this.jurisdiction) defaultMeta.push({ type: 'Jurisdiction', name: this.jurisdiction })
            if (this.showArea) defaultMeta.unshift({ type: 'Area', name: this.area })

            const allMeta  = [
                ...defaultMeta,
                { type: 'Category', name: this.category !== 'undefined' ? this.category : this.page_category },
                { type: 'Group', name: this.group }
            ]

            return this.showAllMeta ? allMeta : defaultMeta
        }
    },

    methods: {
        ...mapActions('Resources', [
            'addToZip',
            'removeFromZip',
            'playVideo',
            'closeVideo',
            'editResource'
        ]),

        handleClick () {
            if (this.isUploadCard) return
            const { type, url } = this

            if (type === 'video') this.playVideo(url)
            else window.open(url, '_blank')

            /* Uncomment the below,
                and visit https://developers.google.com/analytics/devguides/collection/gtagjs/events
                for instructions */
            // this.$gtag.event('View Resource', {
            //     'event_category': 'viewed_resource',
            //     'event_label': `Resource: ${this.name}`,
            //     'value': `Profession ${this.user.profession}`
            // })

            // eslint-disable-next-line
            mixpanel.track('Resource', {
                userProfession: this.user.profession,
                fileName: this.name,
                url: this.url,
                type: this.type,
                area: this.area,
                category: this.category,
                group: this.group,
                jurisdiction: this.jurisdiction
            })
        },

        beforeDestroy() {
            this.closeVideo()
        },
    }
}
</script>

